import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { api } from '../lib/api';

interface PlanFeatures {
  max_sites: number;
  max_storage_bytes: number;
  custom_domain: boolean;
  blog: boolean;
  albums: boolean;
  page_builder: boolean;
  max_pages: number;
  seo_advanced: boolean;
  backup_retention_days: number;
  backup_max_count: number;
  manual_backup: boolean;
  white_label: boolean;
  priority_support: boolean;
  translations: boolean;
}

interface TenantWithPlan {
  id: string;
  name: string;
  slug: string;
  status: string;
  domains: string[];
  plan: string;
  storage_used_bytes: number;
  plan_features: PlanFeatures;
}

interface PlanContextValue {
  tenants: TenantWithPlan[];
  isLoading: boolean;
  isFeatureEnabled: (feature: keyof PlanFeatures) => boolean;
  getLimit: (feature: keyof PlanFeatures) => number;
  features: PlanFeatures | null;
  currentPlan: string;
  refresh: () => Promise<void>;
}

const DEFAULT_FEATURES: PlanFeatures = {
  max_sites: 1,
  max_storage_bytes: 500 * 1024 * 1024,
  custom_domain: false,
  blog: false,
  albums: true,
  page_builder: true,
  max_pages: 3,
  seo_advanced: false,
  backup_retention_days: 7,
  backup_max_count: 4,
  manual_backup: false,
  white_label: false,
  priority_support: false,
  translations: false,
};

const PlanContext = createContext<PlanContextValue | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<TenantWithPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTenants = async () => {
    try {
      const data = await api.get<TenantWithPlan[]>('/tenants/mine');
      setTenants(data);
    } catch {
      setTenants([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  // Use the first tenant's plan features (most users have one tenant)
  const features = tenants[0]?.plan_features ?? DEFAULT_FEATURES;
  const currentPlan = tenants[0]?.plan ?? 'free';

  const isFeatureEnabled = (feature: keyof PlanFeatures): boolean => {
    const value = features[feature];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return true;
  };

  const getLimit = (feature: keyof PlanFeatures): number => {
    const value = features[feature];
    return typeof value === 'number' ? value : 0;
  };

  return (
    <PlanContext.Provider value={{
      tenants,
      isLoading,
      isFeatureEnabled,
      getLimit,
      features,
      currentPlan,
      refresh: fetchTenants,
    }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be inside PlanProvider');
  return ctx;
}
